import { DataSourceMode,  ClickEvent, Button,  LookupModelSelectionEvent, Tab, Label, Table, TableRowCreationEvent, TableContentsChangedEvent,  DataSourceExecutionEvent,  DropdownSelectionEvent, TableRowDisplayEvent, Textbox,  ChangeEvent,  TableRowMoveEvent, TableRowMode } from "@mcleod/components";
import { AutogenLayoutOriginDestinationRate } from "./autogen/AutogenLayoutOriginDestinationRate";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { Api, DisplayType, ModelRow } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { RateUtil } from "../RateUtil";
import { ModelLocation } from "../models/ModelLocation";

const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    cityType: "C",
    stateType: "S",
    locationType: "L",
    countyType: "N",
    useDefault: true,
    lookupProps: {
        city: {
            lookupModelResultField: "full_csz_name"
        },
        freeform: {
            lookupModelAllowFreeform: true
        }
    }
}

const POINT_TO_POINT_ALLOWED = ['C', 'L'];

const MULTISTOP_ALLOWED = ['S', 'Z', 'L'];

export class OriginDestinationRate extends AutogenLayoutOriginDestinationRate {
    private _customerId = null;

    onLoad(): void | Promise<void> {
        this.styleTab(this.tabComments);
        this.layoutComments.displayCopyOrderFields = false;
        this.layoutComments.tableComments.addContentsChangedListener((event: TableContentsChangedEvent) => {
            this.tableCommentOnContentsChanged(event)
        });
        this.layoutComments.tableComments.addRowCreateListener((event: TableRowCreationEvent) => {
            this.tableCommentOnRowCreate(event)
        });

        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxOriginCode, this.textboxOrigin, LOCATION_BY_TYPE_PROPS);
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxIntermediateCode, this.textboxIntermediate, LOCATION_BY_TYPE_PROPS);
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxDestinationCode, this.textboxDestination, LOCATION_BY_TYPE_PROPS);
        RateUtil.setupCountyHandler(this.textboxOriginCode, this.textboxOrigin, [this.textboxOrigCityName, this.textboxOrigState], LOCATION_BY_TYPE_PROPS.countyType);
        RateUtil.setupCountyHandler(this.textboxIntermediateCode, this.textboxIntermediate, [this.textboxIntermediateCityName, this.textboxIntermediateState], LOCATION_BY_TYPE_PROPS.countyType);
        RateUtil.setupCountyHandler(this.textboxDestinationCode, this.textboxDestination, [this.textboxDestCityName, this.textboxDestState], LOCATION_BY_TYPE_PROPS.countyType);

        this.tableMultistop.doOnRowDelete = (row) => {
            this.setMultistopIntermediateFromTable();
        }

        this.addLayoutDataLoadListener(async event => {
            const currentMode = this.mainDataSource.mode;
            if (currentMode != DataSourceMode.SEARCH) {
                if (this.activeRow != null) {
                    this.activeRow.addAfterFieldUpdateListener((event) => {
                        this.syncUIControls();
                    });
                    this.syncUIControls();
                    this.syncRateMethod();
                    this.syncOverrideDistance();
                    this.syncMultistopTable();
                }
            }
        });
    }

    syncUIControls() {
        this.syncPtToPt();
        this.syncDistanceLookup();
        this.syncMultistopSwitch();
        this.syncRateMethod();
    }

    syncPtToPt() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const originType = this.activeRow.getString('orig_code', null);
            const destinationType = this.activeRow.getString('dest_code', null);
            const validOrigin = POINT_TO_POINT_ALLOWED.includes(originType)
                                && !this.textboxOrigin.isEmpty()
                                && ('L' != originType || this.textboxOrigin.lookupModelData?.length == 1);

            const validDest = POINT_TO_POINT_ALLOWED.includes(destinationType)
                                && !this.textboxDestination.isEmpty()
                                && ('L' != destinationType || this.textboxDestination.lookupModelData?.length == 1);

            const validInt = this.textboxIntermediateCode.isEmpty();

            this.switchPtToPt.enabled = validOrigin && validDest && validInt;
            if (!this.switchPtToPt.enabled) {
                this.switchPtToPt.checked = false;
            }
            this.switchPtToPt.disabledTooltip = "Point-to-Point is only allowed for Origin/Destination values of type City/State and single values of type Location";
        }
    }

    syncMultistopSwitch() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const intermediateType = this.activeRow.getString('intermediate_code', null);
            this.switchMultistop.enabled = MULTISTOP_ALLOWED.includes(intermediateType);
            if (!this.switchMultistop.enabled) {
                this.switchMultistop.checked = false;
            }
            this.switchMultistop.disabledTooltip = "Multistop is only allowed for Intermediate values of type Location, State, Zip";
        }
    }

    async syncMultistopTable() {
        const isMultistop = this.switchMultistop.checked;
        this.tableMultistop.visible = isMultistop;
        this.textboxIntermediate.visible = !isMultistop;
        if (isMultistop) {
            const intermediateType = this.activeRow.getString('intermediate_code', null);
            const intermediateStopsString = this.activeRow.getString('intermediate_value', null);
            const intermediateStops = await Promise.all((intermediateStopsString?.split(',') ?? []).map(async(stop, index) => {
                const row = new ModelRow(null, false, { sequence: index + 1, stop_location: stop.trim() });
                const lookupModelData = intermediateType === 'L' ? await new ModelLocation().searchSingle({ id: stop.trim() }, "lme/dispatch/LocationLookup") : { id: stop.trim() };
                row.setLookupModelData("stop_location", lookupModelData)
                return row;
            }));
            this.sourceMultistopDummy.setRowsAndMode(DataSourceMode.NONE, intermediateStops ?? []);
        }
        else {
            this.sourceMultistopDummy.setRowsAndMode(DataSourceMode.NONE, []);
        }
    }

    syncOverrideDistance() {
        if (this.switchPtToPt.checked || (this.switchMultistop.checked && this.areCodesLocation())) {
            this.textboxBillDistance.enabled = true;
        } else {
            this.textboxBillDistance.enabled = false;
            this.activeRow.set("bill_distance", null);
        }
    }

    areCodesLocation() {
        return this.activeRow?.getString('orig_code', null) == 'L' 
            && this.activeRow?.getString('intermediate_code', null) == 'L' 
            && this.activeRow?.getString('dest_code', null) == 'L';
    }

    syncDistanceLookup() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const originType = this.activeRow.getString('orig_code', null);
            const intermediateType = this.activeRow.getString('intermediate_code', null);
            const destinationType = this.activeRow.getString('dest_code', null);

            const isDistanceLookupVisible = (originType == 'C') &&
                                       (intermediateType == null || intermediateType == 'C') &&
                                       (destinationType == 'C');

            this.buttonDistanceLookup.visible = isDistanceLookupVisible;
            this.labelDistanceLookup.visible = isDistanceLookupVisible;
        }
    }

    syncRateMethod() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const rateMethod = this.textboxRateMethod.selectedItem?.value;
            this.syncRateTextboxes(rateMethod);
        }
    }

    syncRateTextboxes(rateMethod: string) {
        const isRateTable = (rateMethod ?? this.textboxRateMethod.selectedItem?.value) === "R"
        isRateTable ? this.textboxRateFreeform.clear() : this.textboxRateLookup.clear();
        this.textboxRateLookup.visible = isRateTable;
        this.textboxRateLookup.required = isRateTable;
        this.textboxRateFreeform.visible = !isRateTable;
        this.textboxRateFreeform.required = !isRateTable;
        this.textboxRateFreeform.displayType = isRateTable ? DisplayType.STRING : DisplayType.FLOAT;
    }

    get customerId() {
        return this._customerId;
    }

    set customerId(value: string) {
        this._customerId = value
    }

    /** This is an event handler for the onClick event of buttonDistanceLookup. */
    async buttonDistanceLookupOnClick(event: ClickEvent) {
        const button = (event.target as Button);
        button.busy = true;
        const rowData = this.mainDataSource.activeRow;

        try {
            const response = await Api.post("lme/dispatch/rate-distance", {
                customer_id: this.customerId,
                orig_code: rowData.getString('orig_code'),
                intermediate_code: rowData.getString('intermediate_code'),
                dest_code: rowData.getString('dest_code'),
                orig_city_name: rowData.getString('orig_city_name'),
                intermediate_city_name: rowData.getString('intermediate_city_name'),
                dest_city_name: rowData.getString('dest_city_name'),
                orig_state: rowData.getString('orig_state'),
                intermediate_state: rowData.getString('intermediate_state'),
                dest_state: rowData.getString('dest_state'),
                orig_zip: rowData.getString('orig_zip'),
                intermediate_zip: rowData.getString('intermediate_zip'),
                dest_zip: rowData.getString('dest_zip'),
            })
            this.labelDistanceLookup.text = response?.data[0]?.distance ?? "--";
        }
        catch (e) {
            CommonDialogs.showError(e);
        }
        finally {
            button.busy = false;
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxOrigin. */
    textboxOriginOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.setCityFields('orig', event);
    }

    textboxIntermediateOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.setCityFields('intermediate', event);
    }

    /** This is an event handler for the onLookupModelSelection event of textboxDestination. */
    textboxDestinationOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.setCityFields('dest', event);
    }

    setCityFields(prefix: string, event: LookupModelSelectionEvent) {
        if (this.mainDataSource.activeRow?.getString(prefix + '_code') == 'C') {
            if (event.hasSelection()) {
                this.mainDataSource?.activeRow?.set(prefix + '_city_name', event.selection?.getString('name'));
                this.mainDataSource?.activeRow?.set(prefix + '_city_no', event.selection?.getNumber('id'));
                this.mainDataSource?.activeRow?.set(prefix + '_state', event.selection?.getString('state_id'));
                this.mainDataSource?.activeRow?.set(prefix + '_zip', event.selection?.getString('zip_code'));
            } else if (event.hasDeselection()) {
                this.mainDataSource?.activeRow?.set(prefix + '_city_name', null);
                this.mainDataSource?.activeRow?.set(prefix + '_city_no', null);
                this.mainDataSource?.activeRow?.set(prefix + '_state', null);
                this.mainDataSource?.activeRow?.set(prefix + '_zip', null);
            }
        }
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.dataSource.rowCount + "";
        }
    }

    tableCommentOnContentsChanged(event: TableContentsChangedEvent) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    tableCommentOnRowCreate(event: TableRowCreationEvent) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    /** This is an event handler for the afterExecution event of sourceComments. */
    sourceCommentsAfterExecution(event: DataSourceExecutionEvent) {
        this.updateTabCountLabel(this.tabComments, this.layoutComments.tableComments);
    }

    /** This is an event handler for the afterDropdownSelection event of textboxRateMethod. */
    textboxRateMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.syncRateMethod();
    }

    /** This is an event handler for the onChange event of switchPtToPt. */
    switchPtToPtOnChange(event: ChangeEvent) {
        this.syncOverrideDistance();
    }

    /** This is an event handler for the afterDropdownSelection event of textboxOriginCode. */
    textboxOriginCodeAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.syncOverrideDistance();
        this.clearODValues('orig');
    }

    /** This is an event handler for the afterDropdownSelection event of textboxIntermediateCode. */
    textboxIntermediateCodeAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.syncOverrideDistance();
        this.clearODValues('intermediate');
        this.syncMultistopTable();
    }
    
    /** This is an event handler for the afterDropdownSelection event of textboxDestinationCode. */
    textboxDestinationCodeAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.syncOverrideDistance();
        this.clearODValues('dest');
    }

    /** This is an event handler for the onRowDisplay event of tableMultistop. */
    tableMultistopOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const tb = tableRow.findComponentById("textboxStopLocation") as Textbox;
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxIntermediateCode, tb, 
            { 
                ...LOCATION_BY_TYPE_PROPS, 
                lookupProps: {
                    ...LOCATION_BY_TYPE_PROPS.lookupProps,
                    all: {
                        lookupModelAllowMultiSelect: false,
                    }
                }
            }
        );
    }

    /** This is an event handler for the onChange event of switchMultistop. */
    switchMultistopOnChange(event: ChangeEvent) {
        this.syncMultistopTable();
        this.syncOverrideDistance();
    }

    setMultistopIntermediateFromTable() {
        const stops = this.tableMultistop.allRows.map(row => row.data.getString('stop_location')).join(',');
        this.activeRow.set('intermediate_value', stops);
    }

    clearODValues(prefix: string) {
        this.mainDataSource?.activeRow?.set(prefix + '_city_name', null);
        this.mainDataSource?.activeRow?.set(prefix + '_city_no', null);
        this.mainDataSource?.activeRow?.set(prefix + '_state', null);
        this.mainDataSource?.activeRow?.set(prefix + '_zip', null);
        this.mainDataSource?.activeRow?.set(prefix + '_value', null);
    }
}
